import type { SingularDayCategory } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { useTranslation } from 'react-i18next'

import { Tooltip } from '@enechain/ecloud-designsystem'
import { FaStarSolid } from '@enechain/ecloud-icons'

import { singularDayCategoryLabelKey } from '~/pages/AdminSingularDay/models/options'

type Props = {
  /** 特異日マスタ登録名 (spec AC-010, 必須・30 文字以内)。 */
  name: string
  category: SingularDayCategory
}

/**
 * 特異日の日付マーカー (design demand/forecast-today.html .dft-special-mark 相当、
 * shared-components.md §8 日付マーカー + ポップオーバー共通仕様)。
 *
 * 呼び出し元 (SectionHeader) が dayType === SINGULAR のときのみ対象日ラベルに隣接して
 * 描画する (AC-08: 特異日でない場合はマーカー自体を描画しない、呼び出し元の責務)。
 * ホバーで「特異日: {name}（{カテゴリ表示名}）」をポップオーバー表示する (AC-06)。
 * バッジ本体には名称を出さない (AC-07) ため、 名称はこのマーカーのホバー内容のみが
 * 情報源になる。
 *
 * カテゴリ表示名 i18n は既存 AdminSingularDay 画面のカテゴリラベル定義を再利用する
 * (design doc §5 frontend 状態モデル、cross-page 参照は WorkflowCaseDetail/
 * CaseHeader.tsx の既存パターンに倣う)。
 */
export const SingularDayMarker = ({
  name,
  category,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const categoryLabel = t(singularDayCategoryLabelKey(category))
  const tooltipText = t(
    'page:demandForecastDaily.header.singularDayMarker.tooltip',
    { name, category: categoryLabel },
  )

  return (
    <Tooltip content={<div>{tooltipText}</div>}>
      {/* role="img" + aria-label でアクセシブルネームを持たせ、getByRole 経由で
          hover 対象を取得できるようにする (design の .dft-special-mark 相当、
          警告 tint の意匠は Text.warningHigh で表現)。 */}
      <span aria-label={tooltipText} role="img">
        <FaStarSolid boxSize="12px" color="Text.warningHigh" />
      </span>
    </Tooltip>
  )
}
